/**
 * API HELPERS - Utilitários para chamadas de API com Firebase Auth
 * ==================================================================
 * Centraliza autenticação e configuração de APIs
 */

import { getAuth } from 'firebase/auth';

// Base URL: Cloudflare Worker em produção, localhost em dev
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV
    ? 'http://localhost:5000'
    : 'https://chatbot-imoveis-api.chatbotimoveis.workers.dev');

/**
 * Obtém token Firebase do usuário autenticado
 * ✅ FORÇA REFRESH se token estiver expirado ou próximo da expiração
 * Isso garante que o login dure 30 dias (via Refresh Token automático)
 *
 * @param forceRefresh - Se true, força renovação do token (padrão: true)
 * @returns Token Firebase ou null
 */
export async function getFirebaseToken(forceRefresh: boolean = true): Promise<string | null> {
  try {
    const auth = getAuth();
    // ✅ forceRefresh=true renova automaticamente tokens expirados
    // O Firebase usa o Refresh Token (válido por 1 ano) para renovar o ID Token
    const token = await auth.currentUser?.getIdToken(forceRefresh);
    return token || null;
  } catch (error) {
    console.error('❌ Erro ao obter token Firebase:', error);
    return null;
  }
}

/**
 * Cria headers padrão para requisições autenticadas
 * @param throwOnMissing - Se true, lança erro quando token não existe (padrão: true)
 * @returns Headers com Authorization, x-user-id e Content-Type
 */
export async function getAuthHeaders(throwOnMissing = true): Promise<HeadersInit> {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const token = await getFirebaseToken();

  if (!token && throwOnMissing) {
    console.warn('⚠️ Token Firebase não disponível - usuário não autenticado');
    throw new Error('Token Firebase não disponível. Faça login novamente.');
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Adicionar x-user-id para compatibilidade com Cloudflare Worker
  if (currentUser?.uid) {
    headers['x-user-id'] = currentUser.uid;
  }

  return headers;
}

/**
 * Wrapper para fetch com autenticação Firebase automática + timeout + retry
 * @param endpoint - Endpoint relativo (ex: /api/organizador-clientes/leads)
 * @param options - Opções do fetch
 * @param timeoutMs - Timeout em ms (padrão: 30000 = 30s)
 * @param retries - Número de tentativas (padrão: 2)
 * @returns Promise com Response
 */
export async function authenticatedFetch(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs: number = 30000,
  retries: number = 2
): Promise<Response> {
  const headers = await getAuthHeaders();

  // Corrigir endpoints legados /sdr-bot para /api/sdr (Cloudflare Worker)
  if (endpoint.startsWith('/sdr-bot')) {
    endpoint = endpoint.replace('/sdr-bot', '/api/sdr');
    console.log(`🔧 [API] Endpoint corrigido: ${endpoint}`);
  }

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

  // Criar AbortController para timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      console.log(
        `🌐 [API] ${options.method || 'GET'} ${url} (tentativa ${attempt + 1}/${retries + 1})`
      );

      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok && attempt < retries) {
        console.warn(`⚠️ [API] Resposta ${response.status}, tentando novamente...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1))); // backoff exponencial
        continue;
      }

      return response;
    } catch (error: any) {
      lastError = error;

      if (error.name === 'AbortError') {
        console.error(`❌ [API] Timeout após ${timeoutMs}ms em ${url}`);
      } else {
        console.error(`❌ [API] Erro na tentativa ${attempt + 1}:`, error.message);
      }

      if (attempt < retries) {
        console.log(`🔄 [API] Tentando novamente em ${(attempt + 1) * 1000}ms...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }
  }

  clearTimeout(timeoutId);

  // Se todas as tentativas falharam, lançar erro
  throw lastError || new Error('Falha na requisição após todas as tentativas');
}

/**
 * Wrapper para GET autenticado
 */
export async function authenticatedGet(endpoint: string): Promise<Response> {
  return authenticatedFetch(endpoint, { method: 'GET' });
}

/**
 * Wrapper para POST autenticado
 */
export async function authenticatedPost(endpoint: string, body: any): Promise<Response> {
  return authenticatedFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Wrapper para PUT autenticado
 */
export async function authenticatedPut(endpoint: string, body: any): Promise<Response> {
  return authenticatedFetch(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Wrapper para DELETE autenticado
 */
export async function authenticatedDelete(endpoint: string, body?: any): Promise<Response> {
  return authenticatedFetch(endpoint, {
    method: 'DELETE',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export { API_BASE_URL };
